import React from 'react';
import {
  Box,
  Button,
  Heading,
  VStack,
  HStack,
  Badge,
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  Text,
  useColorModeValue,
  useClipboard,
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';

// Helper function to clean and format markdown text for ReactMarkdown
const cleanMarkdownText = (text) => {
  if (!text) return '';
  return text.replace(/\n\s+/g, '\n\n').replace(/\n{3,}/g, '\n\n').trim();
};

const PayerResponseDisplay = ({ response }) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const codeBlockBg = useColorModeValue('gray.50', 'gray.800');
  const { hasCopied, onCopy } = useClipboard(response.generated_content.appeal_letter);

  if (!response) {
    return null;
  }

  return (
    <Card bg={cardBg} borderColor={borderColor}>
      <CardHeader>
        <HStack justify="space-between">
          <VStack align="start">
            <Heading size="md">Generated Appeal Letter</Heading>
            <Text fontSize="sm" color="gray.500">
              Response ID: {response.response_id}
            </Text>
          </VStack>
          <Badge colorScheme={response.from_cache ? 'green' : 'blue'}>
            {response.from_cache ? 'Loaded from Cache' : 'Newly Generated'}
          </Badge>
        </HStack>
      </CardHeader>
      <CardBody>
        <VStack spacing={6} align="stretch">
          {/* Appeal Letter */}
          <Box>
            <HStack justify="space-between" mb={2}>
              <Heading size="sm">Appeal Letter</Heading>
              <Button size="sm" onClick={onCopy}>
                {hasCopied ? 'Copied!' : 'Copy Letter'}
              </Button>
            </HStack>
            <Box
              p={4}
              bg={codeBlockBg}
              borderRadius="md"
              border="1px solid"
              borderColor={borderColor}
              maxHeight="500px"
              overflowY="auto"
            >
              <ReactMarkdown>{cleanMarkdownText(response.generated_content.appeal_letter)}</ReactMarkdown>
            </Box>
          </Box>

          {/* Supporting Details */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Box>
              <Heading size="sm" mb={2}>Clinical Justification</Heading>
              <Box p={3} bg={codeBlockBg} borderRadius="md">
                <ReactMarkdown>{response.generated_content.clinical_justification}</ReactMarkdown>
              </Box>
            </Box>
            <Box>
              <Heading size="sm" mb={2}>Supporting Evidence</Heading>
              <Box p={3} bg={codeBlockBg} borderRadius="md">
                <ReactMarkdown>{response.generated_content.supporting_evidence}</ReactMarkdown>
              </Box>
            </Box>
            <Box>
              <Heading size="sm" mb={2}>Policy References</Heading>
              <Box p={3} bg={codeBlockBg} borderRadius="md">
                <ReactMarkdown>{response.generated_content.policy_references}</ReactMarkdown>
              </Box>
            </Box>
          </SimpleGrid>

          {/* Sources Summary */}
          <Box>
            <Heading size="sm" mb={2}>Sources Used</Heading>
            <HStack spacing={4}>
              <Badge>{response.sources_summary.clinical_documents} Clinical Docs</Badge>
              <Badge>{response.sources_summary.medical_necessity_sources} Med-Nec Sources</Badge>
              <Badge>{response.sources_summary.appendix_content_available ? 'Appendix Content Used' : 'No Appendix Content'}</Badge>
            </HStack>
          </Box>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default PayerResponseDisplay;